import type { Survey } from 'types';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { DropdownMenu, DropdownMenuContent } from '@/components/ui/dropdown-menu';
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { MoreHorizontal } from 'lucide-react';
import { useSurvey } from '@/state/surveys/useSurvey';
import { type SurveyAction, SURVEY_ACTION_PROMPTS } from '../survey-copy';

type SurveyRowActionsProps = {
  survey: Survey;
  /** Set on the first row only, for the product tour. */
  tourAnchor?: string;
};

export const SurveyRowActions = ({ survey, tourAnchor }: SurveyRowActionsProps) => {
  const navigate = useNavigate();
  const [pending, setPending] = useState<SurveyAction | null>(null);
  const { startSurvey, endSurvey, duplicateSurvey, archiveSurvey, deleteSurvey } = useSurvey(0);

  const run = async () => {
    if (!pending) return;
    if (pending === 'start') await startSurvey(survey.id);
    if (pending === 'end') await endSurvey(survey.id);
    if (pending === 'archive') await archiveSurvey(survey.id);
    if (pending === 'delete') await deleteSurvey(survey.id);
    if (pending === 'duplicate') {
      // The editor, not the hub: a fresh copy is a draft that exists to be changed.
      const copy = await duplicateSurvey(survey.id);
      if (copy) navigate(`/surveys/${copy.id}/edit`);
    }
    setPending(null);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="border-line text-dim hover:text-foreground hover:bg-hover h-8 w-8 rounded-lg bg-transparent p-0"
          data-tour={tourAnchor}
        >
          <span className="sr-only">Open actions</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        {/*
          Shortcuts, deliberately kept now that the row itself opens the details
          hub. They run the same handlers behind the same prompts as the hub
          header, so the two cannot behave differently.
        */}
        <DropdownMenuItem onClick={() => navigate(`/surveys/${survey.id}`)}>Details</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/surveys/${survey.id}/edit`)}>Edit</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/surveys/${survey.id}/results`)}>Results</DropdownMenuItem>
        <DropdownMenuSeparator />
        {/* Reasons live in the label — a disabled Radix item takes no tooltip. */}
        {/* An archived survey is never served, so the API refuses to start one: unarchive first. */}
        <DropdownMenuItem
          onClick={() => setPending('start')}
          disabled={survey.status === 'Running' || !!survey.archived}
        >
          Start{survey.status === 'Running' && <span className="text-dim ml-1">(already running)</span>}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setPending('end')} disabled={survey.status !== 'Running'}>
          End{survey.status !== 'Running' && <span className="text-dim ml-1">(not running)</span>}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setPending('duplicate')}>Duplicate</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => setPending('archive')}>
          {survey.archived ? 'Unarchive' : 'Archive'}
        </DropdownMenuItem>
        {/* A running survey is collecting data; ending it first is deliberate friction —
            which the reader can only act on if it is stated. */}
        <DropdownMenuItem onClick={() => setPending('delete')} disabled={survey.status === 'Running'}>
          Delete{survey.status === 'Running' && <span className="text-dim ml-1">(end it first)</span>}
        </DropdownMenuItem>
      </DropdownMenuContent>

      {/*
        Mounted always, opened by `pending` — not conditionally mounted already-open.
        Mounting a modal Radix dialog in its open state (and unmounting it while
        open) skips its open/close transitions, and cleanup of the body's
        pointer-events lock is tied to that lifecycle: the leak leaves the whole
        page - nav included - unclickable until a reload.
      */}
      <ConfirmDialog
        open={!!pending}
        title={pending ? SURVEY_ACTION_PROMPTS[pending].title : ''}
        onConfirm={run}
        onCancel={() => setPending(null)}
        confirmLabel={pending === 'end' ? 'End' : undefined}
        confirmVariant={pending === 'end' || pending === 'delete' ? 'destructive' : 'default'}
      >
        {pending && <p className="supporting">{SURVEY_ACTION_PROMPTS[pending].body}</p>}
      </ConfirmDialog>
    </DropdownMenu>
  );
};
